/**
 * POConcentrationView — PO-builder step-4 concentration view (spec § 19 step 4).
 * Presentational only. Day 98 (P8.5): spend/units breakdown by SKU / brand /
 * category / marketplace + total. Day 98.1: + the by_risk 5th axis, per-SKU
 * high-risk badge + "why flagged" tooltip, and advisory guardrail-breach styling.
 * Everything is ADVISORY (highlights/warns, never blocks — spec § 10/§ 11); the
 * risk/breach props are client-derived by POBuilder (poConstraints) and inert
 * (breaches always []) until the Day-98.1b config UI ships.
 *
 * Sparse groups render em-dash labels — a data state, not a defect.
 */
import type { JSX } from 'react';
import clsx from 'clsx';
import { AlertTriangle } from 'lucide-react';
import type { POConcentrationResult } from '../../hooks/usePoConcentration';
import type { ConcentrationBucket, GuardrailBreach, GuardrailKey } from '../../lib/poConstraints';

// DATA_STANDARDS — currency with thousands separator, percentages 1-decimal,
// counts with separator. JetBrains Mono applied via font-mono on the spans.
function fmtUsd(v: number): string {
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtPct(v: number): string {
  return `${v.toFixed(1)}%`;
}
function fmtCount(v: number): string {
  return v.toLocaleString('en-US');
}

/** Rows shown per axis before collapsing the tail into a "+N more" line. */
const TOP_N = 8;

/** Per-SKU risk display, keyed by isg_sku (the by_sku label). `factors` are
 *  human labels (POBuilder maps the RiskFactorKey → RISK_FACTOR_FIELDS label). */
type SkuRiskMap = Record<string, { highRisk: boolean; factors: string[] }>;

interface AxisProps {
  title: string;
  rows: ConcentrationBucket[];
  totalAmount: number;
  /** The guardrail whose breach highlights this axis's top bucket (index 0 — the
   *  bucket every max-% guardrail measures; for by_risk that is the High-risk
   *  bucket). Undefined for axes with no mapped guardrail. */
  breachKey?: GuardrailKey;
  breaches?: GuardrailBreach[];
  /** by_sku only: the per-SKU high-risk badge + why-flagged tooltip. */
  skuRisk?: SkuRiskMap;
}

function ConcentrationAxis({
  title,
  rows,
  totalAmount,
  breachKey,
  breaches,
  skuRisk,
}: AxisProps): JSX.Element {
  const shown = rows.slice(0, TOP_N);
  const tail = rows.slice(TOP_N);
  const tailAmount = tail.reduce((s, r) => s + r.amount, 0);
  const axisBreached =
    !!breachKey && (breaches?.some((b) => b.key === breachKey && b.breached) ?? false);
  return (
    <div>
      <div className="text-2xs uppercase tracking-wider text-ink-muted mb-1.5">{title}</div>
      {rows.length === 0 ? (
        <div className="text-xs text-ink-muted">—</div>
      ) : (
        <div className="space-y-1.5">
          {shown.map((r, i) => {
            const pct = totalAmount > 0 ? (r.amount / totalAmount) * 100 : 0;
            // The max-% guardrail measures the TOP bucket (index 0) → warn-tint it.
            const breachRow = axisBreached && i === 0;
            const risk = skuRisk?.[r.label ?? ''];
            return (
              <div
                key={`${r.label ?? 'null'}-${i}`}
                data-testid={breachRow ? 'concentration-breach-row' : undefined}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span
                      className="truncate text-xs text-ink-primary"
                      title={r.title ?? r.label ?? undefined}
                    >
                      {r.label ?? <span className="text-ink-muted">—</span>}
                    </span>
                    {risk?.highRisk && (
                      <span
                        className="flex-shrink-0 rounded bg-warning/15 px-1 text-[10px] font-medium uppercase tracking-wide text-warning"
                        title={
                          risk.factors.length
                            ? `High risk: ${risk.factors.join(', ')}`
                            : 'High risk'
                        }
                        data-testid="sku-risk-badge"
                      >
                        risk
                      </span>
                    )}
                  </span>
                  <span className="flex flex-shrink-0 items-baseline gap-2">
                    <span className="font-mono text-2xs text-ink-muted">{fmtCount(r.units)} u</span>
                    <span
                      className={clsx(
                        'font-mono text-xs',
                        r.amount < 0 ? 'text-danger' : 'text-ink-bright'
                      )}
                    >
                      {fmtUsd(r.amount)}
                    </span>
                    <span
                      className={clsx(
                        'w-12 text-right font-mono text-2xs',
                        breachRow ? 'text-warning' : 'text-ink-muted'
                      )}
                    >
                      {fmtPct(pct)}
                    </span>
                  </span>
                </div>
                {/* Proportion bar — the "visual allocation" per spec § 10. */}
                <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-surface-3">
                  <div
                    className={clsx('h-full', breachRow ? 'bg-warning' : 'bg-accent/60')}
                    style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                  />
                </div>
              </div>
            );
          })}
          {tail.length > 0 && (
            <div className="pt-0.5 text-2xs text-ink-muted">
              +{fmtCount(tail.length)} more · {fmtUsd(tailAmount)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** TONE_STANDARDS § 9 advisory line for a breached guardrail — flat + specific,
 *  never alarmist, never a block. */
function breachMessage(b: GuardrailBreach): string {
  const pct = fmtPct(b.actual);
  const lim = `${b.limit}%`;
  switch (b.key) {
    case 'max_pct_single_sku':
      return `${pct} of spend on a single SKU — over your ${lim} guardrail`;
    case 'max_dollar_single_sku':
      return `${fmtUsd(b.actual)} on a single SKU — over your ${fmtUsd(b.limit)} guardrail`;
    case 'max_pct_brand':
      return `${pct} of spend on a single brand — over your ${lim} guardrail`;
    case 'max_pct_category':
      return `${pct} of spend on a single category — over your ${lim} guardrail`;
    case 'max_pct_marketplace':
      return `${pct} of spend on a single marketplace — over your ${lim} guardrail`;
    case 'max_pct_high_risk':
      return `${pct} of spend in high-risk products — over your ${lim} guardrail`;
    case 'max_pct_slow_movers':
      return `${pct} of spend in slow movers — over your ${lim} guardrail`;
    case 'max_pct_new':
      return `${pct} of spend in new products — over your ${lim} guardrail`;
    case 'min_skus':
      return `${fmtCount(b.actual)} SKUs — under your minimum of ${fmtCount(b.limit)}`;
    default:
      return '';
  }
}

interface POConcentrationViewProps {
  data: POConcentrationResult | null;
  loading: boolean;
  error: string | null;
  // Day 98.1 — client-derived by POBuilder from data + the resolved risk/guardrail
  // config. Optional so the Day-98 callers (and tests) compile unchanged.
  byRisk?: ConcentrationBucket[];
  skuRisk?: SkuRiskMap;
  breaches?: GuardrailBreach[];
}

export default function POConcentrationView({
  data,
  loading,
  error,
  byRisk,
  skuRisk,
  breaches,
}: POConcentrationViewProps): JSX.Element {
  const activeBreaches = (breaches ?? []).filter((b) => b.breached);
  return (
    <div className="arbi-card p-4" data-testid="po-concentration">
      <h2 className="text-sm font-semibold text-ink-bright">Concentration</h2>
      <p className="mb-4 mt-0.5 text-2xs text-ink-muted">
        How this PO&apos;s spend is distributed — advisory; review before submitting.
      </p>
      {loading ? (
        <div className="text-xs text-ink-muted">Calculating concentration…</div>
      ) : error ? (
        <div className="text-xs text-ink-muted">{error}</div>
      ) : !data || data.total.units === 0 ? (
        <div className="text-xs text-ink-muted">—</div>
      ) : (
        <>
          <div className="mb-4 flex items-baseline gap-6 border-b border-border-subtle pb-3">
            <div>
              <div className="text-2xs uppercase tracking-wider text-ink-muted">Total Spend</div>
              <div className="font-mono text-sm text-ink-bright">{fmtUsd(data.total.amount)}</div>
            </div>
            <div>
              <div className="text-2xs uppercase tracking-wider text-ink-muted">Total Units</div>
              <div className="font-mono text-sm text-ink-bright">{fmtCount(data.total.units)}</div>
            </div>
          </div>
          {/* Day 98.1 — advisory guardrail breaches (spec § 10 "highlights, never
              blocks"). Inert until the 98.1b config UI lets a partner set a limit. */}
          {activeBreaches.length > 0 && (
            <div className="mb-4 space-y-1" data-testid="concentration-breaches">
              {activeBreaches.map((b) => (
                <div
                  key={b.key}
                  className="flex items-start gap-1.5 rounded border border-warning/30 bg-warning/10 p-2 text-2xs text-warning"
                >
                  <AlertTriangle size={12} className="mt-px flex-shrink-0" />
                  <span>{breachMessage(b)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-2">
            <ConcentrationAxis
              title="By SKU"
              rows={data.by_sku}
              totalAmount={data.total.amount}
              breachKey="max_pct_single_sku"
              breaches={breaches}
              skuRisk={skuRisk}
            />
            <ConcentrationAxis
              title="By Brand"
              rows={data.by_brand}
              totalAmount={data.total.amount}
              breachKey="max_pct_brand"
              breaches={breaches}
            />
            <ConcentrationAxis
              title="By Category"
              rows={data.by_category}
              totalAmount={data.total.amount}
              breachKey="max_pct_category"
              breaches={breaches}
            />
            <ConcentrationAxis
              title="By Marketplace"
              rows={data.by_marketplace}
              totalAmount={data.total.amount}
              breachKey="max_pct_marketplace"
              breaches={breaches}
            />
            {byRisk && byRisk.length > 0 && (
              <ConcentrationAxis
                title="By Risk"
                rows={byRisk}
                totalAmount={data.total.amount}
                breachKey="max_pct_high_risk"
                breaches={breaches}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
